import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useForm, FormProvider } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNoteSticky, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import Accordion from '../NavTab/Accordion';
import Textarea from '../Form/Fields/Textarea';
import Button from '../Form/Button';
import { formatRelativeDate, formatFullDate } from '../../utils/FormatDate';
import './PatientNotes.scss';
import { API_BASE_URL } from '../../api';

const PatientNoteEntry = ({ note, onSave, onDelete }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(note.comment);
    const [saving, setSaving] = useState(false);

    const startEdit = () => {
        setDraft(note.comment);
        setEditing(true);
    };

    const cancelEdit = () => setEditing(false);

    const save = async () => {
        if (!draft.trim() || draft.trim() === note.comment) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(note.id, draft.trim());
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="patient-note-entry">
            <div className="patient-note-meta">
                <span className="patient-note-author">{note.createdByUsername}</span>
                <span
                    className="patient-note-date"
                    data-tooltip-id="tooltip"
                    data-tooltip-content={formatFullDate(note.createdAt)}
                >
                    {formatRelativeDate(note.createdAt)}
                </span>

                {!editing && (
                    <div className="patient-note-actions">
                        <button type="button" className="patient-note-action-btn" title="Modifier" onClick={startEdit}>
                            <FontAwesomeIcon icon={faPen} />
                        </button>
                        <Button
                            className="patient-note-action-btn patient-note-action-btn--danger"
                            onConfirm={() => onDelete(note.id)}
                            title="Supprimer"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>
                )}
            </div>

            {editing ? (
                <div className="patient-note-edit">
                    <textarea
                        className="patient-note-edit__textarea"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        autoFocus
                    />
                    <div className="patient-note-edit__actions">
                        <button type="button" className="patient-note-edit__cancel" onClick={cancelEdit} disabled={saving}>
                            Annuler
                        </button>
                        <button type="button" className="patient-note-edit__save" onClick={save} disabled={saving}>
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                </div>
            ) : (
                <p className="patient-note-comment">{note.comment}</p>
            )}
        </div>
    );
};

const PatientNotes = () => {
    const { id } = useParams();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const methods = useForm({ defaultValues: { comment: '' } });
    const { handleSubmit, control, reset } = methods;

    const loadNotes = () => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/api/v1/patients/${id}/notes`, { withCredentials: true })
            .then(res => setNotes(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadNotes(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = async (data) => {
        if (!data.comment?.trim()) return;
        setSubmitting(true);
        try {
            const { data: note } = await axios.post(
                `${API_BASE_URL}/api/v1/patients/${id}/notes`,
                { comment: data.comment.trim() },
                { withCredentials: true }
            );
            setNotes(prev => [note, ...prev]);
            reset({ comment: '' });
        } catch {
            // handled by the global axios error toast
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = (noteId, comment) => axios
        .put(`${API_BASE_URL}/api/v1/patients/${id}/notes/${noteId}`, { comment }, { withCredentials: true })
        .then(({ data: updated }) => setNotes(prev => prev.map(n => (n.id === noteId ? updated : n))));

    const handleDelete = (noteId) => axios
        .delete(`${API_BASE_URL}/api/v1/patients/${id}/notes/${noteId}`, { withCredentials: true })
        .then(() => setNotes(prev => prev.filter(n => n.id !== noteId)))
        .catch(() => {});

    return (
        <div className="patient-notes-wrap">
            <Accordion
                id="patientNotes"
                parent="patient_information_container"
                title="Notes"
                icon={faNoteSticky}
            >
                <FormProvider {...methods}>
                    <div className="patient-notes">
                        <form className="patient-notes-form" onSubmit={handleSubmit(onSubmit)}>
                            <Textarea
                                name="comment"
                                control={control}
                                placeholder="Ajouter une note sur ce patient..."
                                validationRules={{ required: true }}
                            />
                            <div className="patient-notes-actions">
                                <button type="submit" className="btn btn-primary fw-bold" disabled={submitting}>
                                    {submitting ? 'Ajout...' : 'Ajouter'}
                                </button>
                            </div>
                        </form>

                        {loading ? (
                            <div className="patient-notes-loading">
                                <span className="spinner-border spinner-border-sm" />
                            </div>
                        ) : notes.length > 0 ? (
                            <div className="patient-notes-history">
                                {notes.map(note => (
                                    <PatientNoteEntry
                                        key={note.id}
                                        note={note}
                                        onSave={handleUpdate}
                                        onDelete={handleDelete}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="patient-notes-empty">
                                <FontAwesomeIcon icon={faNoteSticky} />
                                Aucune note pour ce patient
                            </div>
                        )}
                    </div>
                </FormProvider>
            </Accordion>
        </div>
    );
};

export default PatientNotes;
